import NavbarV2 from '../../components/navbarV2/NavbarV2';
import './Packing.css';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { ClipLoader } from 'react-spinners';
import { useDropzone } from 'react-dropzone';
import ShippingLabel from '../../components/ShippingLabel/ShippingLabel';
import BarcodeCameraScanner from '../../components/packing/BarcodeCameraScanner';
import ScanProgressRing from '../../components/packing/ScanProgressRing';
import { createHtmlPrintJob } from '../../utils/printJob';
import {
  buildFulfillmentLines,
  buildFulfillmentLinesFromInvoice,
  fulfillmentComplete,
  groupFulfillmentLinesByBundle,
  resolveScanMatch,
} from '../../utils/fulfillmentLines';
import { useScanFeedback } from '../../hooks/useScanFeedback';
import { extractIkeaInvoiceLinesFromPdf } from '../../utils/parseIkeaInvoicePdf';
import { formatMoney } from '../../utils/formatMoney';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';

const STEPS = {
  SELECT: 'select',
  PACK: 'pack',
};

/** List screen: pack into queue vs bulk “in warehouse” customer emails vs IKEA invoice verify */
const LIST_MODE = {
  PACK: 'pack',
  NOTIFY: 'notify',
  INVOICE: 'invoice',
  IKEA_BILL: 'ikeaBill',
};

/** PACK step session source */
const PACK_SESSION = {
  ORDERS: 'orders',
  IKEA_INVOICE: 'ikeaInvoice',
};

const SCAN_MODE = {
  CAMERA: 'camera',
  KEYBOARD: 'keyboard',
};

const INVOICE_LINE_SORT = {
  REMAINING_DESC: 'remaining-desc',
  REMAINING_ASC: 'remaining-asc',
  PDF_ORDER: 'pdf-order',
};

function statusClass(status) {
  switch (status) {
    case 'SUBMITTED':
      return 'packv2-status--submitted';
    case 'APPROVED':
      return 'packv2-status--approved';
    case 'DECLINED':
      return 'packv2-status--declined';
    case 'SHIPPED':
      return 'packv2-status--shipped';
    default:
      return 'packv2-status--default';
  }
}

function Packing() {
  const navigate = useNavigate();
  const [step, setStep] = useState(STEPS.SELECT);
  const [orders, setOrders] = useState([]);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState('');
  const prevSearch = useRef(null);
  const [debouncedQuery, flushQuery] = useDebouncedValue(query, 350);
  const [debouncedFilter, flushFilter] = useDebouncedValue(filter, 350);
  const [selectedIds, setSelectedIds] = useState([]);
  const [queueIds, setQueueIds] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [activeOrder, setActiveOrder] = useState(null);
  const [lines, setLines] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [loadingOrder, setLoadingOrder] = useState(false);
  const [scanValue, setScanValue] = useState('');
  const [printing, setPrinting] = useState(false);
  const [labelsToPrint, setLabelsToPrint] = useState(0);
  const [labelPrintPromptOpen, setLabelPrintPromptOpen] = useState(false);
  const [labelPrintCount, setLabelPrintCount] = useState(1);
  const [markingShipped, setMarkingShipped] = useState(false);
  const [listMode, setListMode] = useState(LIST_MODE.PACK);
  const [notifyMessage, setNotifyMessage] = useState('');
  const [sendingWarehouseEmails, setSendingWarehouseEmails] = useState(false);
  const [packSessionKind, setPackSessionKind] = useState(PACK_SESSION.ORDERS);
  const [invoiceFileMeta, setInvoiceFileMeta] = useState({
    filename: null,
    invoiceNumber: null,
  });
  const [invoiceParsing, setInvoiceParsing] = useState(false);
  /** Parsed IKEA PDF rows for Xero purchase bill (one draft ACCPAY bill). */
  const [ikeaBillData, setIkeaBillData] = useState(null);
  const [ikeaBillPreview, setIkeaBillPreview] = useState(null);
  const [ikeaBillPreviewLoading, setIkeaBillPreviewLoading] = useState(false);
  const [ikeaBillPreviewError, setIkeaBillPreviewError] = useState(null);
  const [billPosting, setBillPosting] = useState(false);
  const [scanMode, setScanMode] = useState(SCAN_MODE.KEYBOARD);
  const [scanPanelState, setScanPanelState] = useState('idle');
  const [scanBeepOn, setScanBeepOn] = useState(
    () => localStorage.getItem('pack-scan-beep') !== 'false',
  );
  const [invoiceLineSort, setInvoiceLineSort] = useState(INVOICE_LINE_SORT.REMAINING_DESC);
  const [invoiceHideComplete, setInvoiceHideComplete] = useState(true);
  const scanRef = useRef(null);
  const lineRefs = useRef({});
  const lastWedgeScanRef = useRef({ code: '', t: 0 });
  const linesRef = useRef(lines);
  linesRef.current = lines;

  const { lastScan, flashLineId, shake, progress, reportSuccess, reportError, reset: resetScanFeedback } =
    useScanFeedback({ lines, beepEnabled: scanBeepOn });

  useEffect(() => {
    document.body.classList.add('packing-page');
    return () => document.body.classList.remove('packing-page');
  }, []);

  useEffect(() => {
    if (
      localStorage.getItem('account') == null ||
      localStorage.getItem('admin') !== 'true'
    ) {
      navigate('/login');
    }
  }, [navigate]);

  useEffect(() => {
    if (listMode !== LIST_MODE.IKEA_BILL) {
      setIkeaBillData(null);
      setIkeaBillPreview(null);
      setIkeaBillPreviewError(null);
      setIkeaBillPreviewLoading(false);
    }
  }, [listMode]);

  useEffect(() => {
    if (listMode !== LIST_MODE.IKEA_BILL || !ikeaBillData?.lines?.length) {
      setIkeaBillPreview(null);
      setIkeaBillPreviewError(null);
      setIkeaBillPreviewLoading(false);
      return undefined;
    }

    let cancelled = false;
    setIkeaBillPreviewLoading(true);
    setIkeaBillPreviewError(null);

    fetch(`${process.env.REACT_APP_API}/api/xeroIkeaPurchaseBillPreview`, {
      method: 'POST',
      cache: 'no-cache',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        invoiceNumber: ikeaBillData.invoiceNumber,
        filename: ikeaBillData.filename,
        lines: ikeaBillData.lines.map((row) => ({
          sku: row.sku,
          qty: row.qty,
          label: row.label,
        })),
      }),
    })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (cancelled) return;
        if (response.ok && data.ok && data.preview) {
          setIkeaBillPreview(data.preview);
          setIkeaBillPreviewError(null);
        } else if (response.status === 401 || response.status === 403) {
          navigate('/');
        } else {
          setIkeaBillPreview(null);
          setIkeaBillPreviewError(data.error || 'Could not build Xero bill preview.');
        }
      })
      .catch(() => {
        if (!cancelled) {
          setIkeaBillPreview(null);
          setIkeaBillPreviewError('Could not build Xero bill preview.');
        }
      })
      .finally(() => {
        if (!cancelled) setIkeaBillPreviewLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [ikeaBillData, listMode, navigate]);

  const fetchOrders = useCallback(
    (index, searchQ, searchF) => {
      setLoadingList(true);
      let url = `${process.env.REACT_APP_API}/api/getAllOrders`;
      const params = new URLSearchParams();
      if (searchQ.length > 0) params.set('query', searchQ);
      if (searchF.length > 0) params.set('filter', searchF);
      params.set('page', String(index));
      url += `?${params.toString()}`;

      fetch(url, {})
        .then((response) => {
          if (response.status === 200) {
            response.json().then((data) => setOrders(data));
          } else if (response.status === 401 || response.status === 403) {
            navigate('/');
          }
        })
        .finally(() => setLoadingList(false));
    },
    [navigate]
  );

  useEffect(() => {
    if (!prevSearch.current) {
      prevSearch.current = { q: debouncedQuery, f: debouncedFilter };
      fetchOrders(page, debouncedQuery, debouncedFilter);
      return;
    }
    const searchChanged =
      prevSearch.current.q !== debouncedQuery ||
      prevSearch.current.f !== debouncedFilter;

    if (searchChanged) {
      prevSearch.current = { q: debouncedQuery, f: debouncedFilter };
      if (page !== 1) {
        setPage(1);
      } else {
        fetchOrders(1, debouncedQuery, debouncedFilter);
      }
      return;
    }

    fetchOrders(page, debouncedQuery, debouncedFilter);
  }, [page, debouncedQuery, debouncedFilter, fetchOrders]);

  useEffect(() => {
    if (
      step !== STEPS.PACK ||
      packSessionKind !== PACK_SESSION.ORDERS ||
      !queueIds[activeIndex]
    ) {
      return undefined;
    }
    const id = queueIds[activeIndex];
    setLoadingOrder(true);
    fetch(`${process.env.REACT_APP_API}/api/getOrder/${id}`, {})
      .then((response) => {
        if (response.status === 200) {
          response.json().then((data) => {
            setActiveOrder(data);
            setLines(buildFulfillmentLines(data));
          });
        } else {
          toast.error('Could not load order');
        }
      })
      .finally(() => setLoadingOrder(false));
    return undefined;
  }, [step, queueIds, activeIndex, packSessionKind]);

  useEffect(() => {
    if (step !== STEPS.PACK) return undefined;
    const coarse = window.matchMedia('(pointer: coarse)').matches;
    setScanMode(coarse ? SCAN_MODE.CAMERA : SCAN_MODE.KEYBOARD);
    return undefined;
  }, [step, activeIndex, packSessionKind]);

  useEffect(() => {
    if (
      step === STEPS.PACK &&
      scanRef.current &&
      !printing &&
      scanMode === SCAN_MODE.KEYBOARD &&
      !loadingOrder &&
      !markingShipped
    ) {
      const coarse = window.matchMedia('(pointer: coarse)').matches;
      if (coarse) return undefined;
      const t = setTimeout(() => scanRef.current?.focus({ preventScroll: true }), 100);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [step, activeIndex, printing, loadingOrder, packSessionKind, scanMode, markingShipped]);

  const onInvoicePdfDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      toast.error('Please upload a PDF invoice.');
      return;
    }
    setInvoiceParsing(true);
    try {
      const { invoiceNumber, lines: parsedRows } = await extractIkeaInvoiceLinesFromPdf(file);
      if (!parsedRows.length) {
        toast.error(
          'No article rows found. Use an IKEA Italia invoice PDF (text export or scanned). We look for 8-digit “N. articolo”, VAT %, and quantity.',
        );
        return;
      }
      setInvoiceFileMeta({
        filename: file.name,
        invoiceNumber: invoiceNumber || null,
      });
      setPackSessionKind(PACK_SESSION.IKEA_INVOICE);
      setLines(buildFulfillmentLinesFromInvoice(parsedRows));
      setQueueIds([]);
      setActiveIndex(0);
      setActiveOrder(null);
      resetScanFeedback();
      setStep(STEPS.PACK);
      toast.success(`Loaded ${parsedRows.length} SKU line(s) from the invoice PDF.`);
    } catch (err) {
      console.error('[packing] IKEA invoice PDF', err);
      toast.error('Could not read that PDF.');
    } finally {
      setInvoiceParsing(false);
    }
  }, []);

  const onIkeaBillPdfDrop = useCallback(async (acceptedFiles) => {
    const file = acceptedFiles?.[0];
    if (!file) return;
    if (file.type !== 'application/pdf') {
      toast.error('Please upload a PDF invoice.');
      return;
    }
    setInvoiceParsing(true);
    try {
      const { invoiceNumber, lines: parsedRows } = await extractIkeaInvoiceLinesFromPdf(file, {
        forPurchaseBill: true,
      });
      if (!parsedRows.length) {
        toast.error(
          'No article rows found. Use an IKEA Italia invoice PDF (text export or scanned). We look for 8-digit “N. articolo”, VAT %, and quantity.',
        );
        return;
      }
      setIkeaBillData({
        filename: file.name,
        invoiceNumber: invoiceNumber || null,
        lines: parsedRows,
      });
      toast.success(`Loaded ${parsedRows.length} line(s). Resolving catalog prices for Xero preview…`);
    } catch (err) {
      console.error('[packing] IKEA bill PDF', err);
      toast.error('Could not read that PDF.');
    } finally {
      setInvoiceParsing(false);
    }
  }, []);

  const invoiceDropzone = useDropzone({
    onDrop: onInvoicePdfDrop,
    accept: { 'application/pdf': ['.pdf'] },
    maxFiles: 1,
    disabled: invoiceParsing,
  });

  const ikeaBillDropzone = useDropzone({
    onDrop: onIkeaBillPdfDrop,
    accept: { 'application/pdf': ['.pdf'] },
    maxFiles: 1,
    disabled: invoiceParsing,
  });

  const postIkeaBillToXero = useCallback(() => {
    if (!ikeaBillData?.lines?.length || !ikeaBillPreview?.ready) return;
    const n = ikeaBillPreview.lineCount;
    const ref = ikeaBillData.invoiceNumber || ikeaBillData.filename || 'this invoice';
    if (
      !window.confirm(
        `Create one draft supplier bill in Xero — ${n} line(s) from ${ref}? Lines use COGS account 310, 8th Directive 0% tax; unit prices are VAT-inclusive from your product catalog (or Xero items for IKEA service SKUs).`,
      )
    ) {
      return;
    }
    setBillPosting(true);
    fetch(`${process.env.REACT_APP_API}/api/xeroIkeaPurchaseBill`, {
      method: 'POST',
      cache: 'no-cache',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        invoiceNumber: ikeaBillData.invoiceNumber,
        filename: ikeaBillData.filename,
        lines: ikeaBillData.lines.map((r) => ({
          sku: r.sku,
          qty: r.qty,
          label: r.label,
        })),
      }),
    })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (response.ok && data.ok) {
          toast.success(
            `Draft bill in Xero created${
              data.xeroInvoiceNumber != null ? ` (#${data.xeroInvoiceNumber})` : ''
            }.`,
          );
        } else if (response.status === 401 || response.status === 403) {
          navigate('/');
        } else {
          toast.error(data.error || 'Could not create Xero bill.');
        }
      })
      .catch(() => toast.error('Could not create Xero bill.'))
      .finally(() => setBillPosting(false));
  }, [ikeaBillData, ikeaBillPreview, navigate]);

  const toggleSelected = useCallback((id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  }, []);

  const startPacking = () => {
    if (selectedIds.length === 0) {
      toast.warning('Select at least one order');
      return;
    }
    setPackSessionKind(PACK_SESSION.ORDERS);
    setInvoiceFileMeta({ filename: null, invoiceNumber: null });
    setScanMode(SCAN_MODE.KEYBOARD);
    setQueueIds([...selectedIds]);
    setActiveIndex(0);
    resetScanFeedback();
    setStep(STEPS.PACK);
  };

  const sendWarehouseBulkEmails = () => {
    const msg = notifyMessage.trim();
    if (selectedIds.length === 0) {
      toast.warning('Select at least one order');
      return;
    }
    if (!msg) {
      toast.warning('Write the message to send (e.g. expected delivery day)');
      return;
    }
    if (
      !window.confirm(
        `Send the warehouse / shipping message by email to ${selectedIds.length} customer(s)?`
      )
    ) {
      return;
    }
    setSendingWarehouseEmails(true);
    fetch(`${process.env.REACT_APP_API}/api/sendWarehouseArrivalEmails`, {
      method: 'POST',
      cache: 'no-cache',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ orderIds: selectedIds, message: msg }),
    })
      .then(async (response) => {
        const data = await response.json().catch(() => ({}));
        if (response.status === 200 && data.sent) {
          const { sent = [], skipped = [], failed = [] } = data;
          const parts = [`Sent: ${sent.length}`];
          if (skipped.length) parts.push(`skipped: ${skipped.length}`);
          if (failed.length) parts.push(`failed: ${failed.length}`);
          toast.success(parts.join(' · '));
          if (skipped.length || failed.length) {
            console.warn('[packing] warehouse emails', data);
          }
        } else if (response.status === 401 || response.status === 403) {
          navigate('/');
        } else {
          toast.error(data.error || 'Could not send emails.');
        }
      })
      .catch(() => toast.error('Could not send emails.'))
      .finally(() => setSendingWarehouseEmails(false));
  };

  const applyDecodedScan = useCallback(
    (rawCode, opts = {}) => {
      const { silentNoMatch } = opts;
      const rawTrim = String(rawCode ?? '').trim();
      if (!rawTrim) return false;

      const resolved = resolveScanMatch(linesRef.current, rawTrim);
      if (resolved.status === 'empty') return false;

      if (resolved.status === 'already_complete') {
        if (!silentNoMatch) {
          const label = resolved.completedMatches?.[0]?.label || resolved.normalized;
          reportError({
            code: resolved.normalized,
            rawTrim,
            message: `Already complete — ${label}`,
          });
          setScanPanelState('nomatch');
          setTimeout(() => setScanPanelState('idle'), 450);
        }
        return false;
      }

      if (resolved.status === 'no_match') {
        if (!silentNoMatch) {
          const msg =
            packSessionKind === PACK_SESSION.IKEA_INVOICE
              ? `Not on this invoice: "${resolved.normalized}"`
              : resolved.normalized !== rawTrim
                ? `No match for "${resolved.normalized}" (scanned: "${rawTrim}")`
                : `No match for "${resolved.normalized}"`;
          reportError({ code: resolved.normalized, rawTrim, message: msg });
          setScanPanelState('nomatch');
          setTimeout(() => setScanPanelState('idle'), 450);
        }
        return false;
      }

      const matched = resolved.line;
      const nextRemaining = Math.max(0, matched.remaining - 1);

      setLines((prev) =>
        prev.map((row) =>
          row.id === matched.id ? { ...row, remaining: nextRemaining } : row,
        ),
      );
      reportSuccess({ ...matched, remaining: nextRemaining });
      setScanPanelState('success');
      setTimeout(() => setScanPanelState('idle'), 300);

      const el = lineRefs.current[matched.id];
      if (el?.scrollIntoView) {
        el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
      return true;
    },
    [packSessionKind, reportError, reportSuccess],
  );

  const submitScan = useCallback(
    (raw) => {
      const rawTrim = String(raw ?? '').trim();
      if (!rawTrim || loadingOrder || markingShipped) return;

      const now = Date.now();
      const { code, t } = lastWedgeScanRef.current;
      if (rawTrim === code && now - t < 400) {
        setScanValue('');
        return;
      }
      lastWedgeScanRef.current = { code: rawTrim, t: now };

      applyDecodedScan(rawTrim);
      setScanValue('');
      requestAnimationFrame(() => scanRef.current?.focus({ preventScroll: true }));
    },
    [applyDecodedScan, loadingOrder, markingShipped],
  );

  const handleCameraDecoded = useCallback(
    (text) => applyDecodedScan(text, { silentNoMatch: false }),
    [applyDecodedScan],
  );

  const onScanKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === 'Tab' || e.key === 'NumpadEnter') {
      e.preventDefault();
      submitScan(e.currentTarget.value);
    }
  };

  const goPrev = () => {
    if (activeIndex <= 0) return;
    setActiveIndex(activeIndex - 1);
    setScanValue('');
    resetScanFeedback();
  };

  const goNext = () => {
    if (activeIndex >= queueIds.length - 1) return;
    setActiveIndex(activeIndex + 1);
    setScanValue('');
    resetScanFeedback();
  };

  const handlePrintLabel = () => {
    if (!activeOrder) return;
    setLabelPrintCount(1);
    setLabelPrintPromptOpen(true);
  };

  const confirmPrintLabels = () => {
    if (!activeOrder) return;
    const count = Math.min(99, Math.max(1, Number(labelPrintCount) || 1));
    void createHtmlPrintJob({
      orderId: activeOrder._id,
      orderNumber: activeOrder.orderNumber,
      labelCount: count,
    });
    setLabelPrintPromptOpen(false);
    setLabelsToPrint(count);
    setPrinting(true);
  };

  useEffect(() => {
    if (!printing || labelsToPrint < 1) return undefined;
    const t = setTimeout(() => window.print(), 400);
    return () => clearTimeout(t);
  }, [printing, labelsToPrint]);

  useEffect(() => {
    const afterPrint = () => {
      setPrinting(false);
      setLabelsToPrint(0);
    };
    window.addEventListener('afterprint', afterPrint);
    return () => window.removeEventListener('afterprint', afterPrint);
  }, []);

  const done = useMemo(() => fulfillmentComplete(lines), [lines]);

  const nextLine = useMemo(() => lines.find((l) => l.remaining > 0) ?? null, [lines]);

  const displayLines = useMemo(() => {
    if (packSessionKind !== PACK_SESSION.IKEA_INVOICE) return lines;
    let rows = lines.map((line, index) => ({ line, index }));
    if (invoiceHideComplete) {
      rows = rows.filter(({ line }) => line.remaining > 0);
    }
    if (invoiceLineSort === INVOICE_LINE_SORT.REMAINING_DESC) {
      rows.sort(
        (a, b) =>
          b.line.remaining - a.line.remaining ||
          a.line.label.localeCompare(b.line.label),
      );
    } else if (invoiceLineSort === INVOICE_LINE_SORT.REMAINING_ASC) {
      rows.sort(
        (a, b) =>
          a.line.remaining - b.line.remaining ||
          a.line.label.localeCompare(b.line.label),
      );
    }
    return rows.map(({ line }) => line);
  }, [lines, packSessionKind, invoiceHideComplete, invoiceLineSort]);

  const packingLineGroups = useMemo(() => {
    const source =
      packSessionKind === PACK_SESSION.IKEA_INVOICE ? displayLines : lines;
    return groupFulfillmentLinesByBundle(source);
  }, [lines, displayLines, packSessionKind]);

  const resetToSelect = () => {
    setStep(STEPS.SELECT);
    setPackSessionKind(PACK_SESSION.ORDERS);
    setInvoiceFileMeta({ filename: null, invoiceNumber: null });
    setIkeaBillData(null);
    setIkeaBillPreview(null);
    setIkeaBillPreviewError(null);
    setScanMode(SCAN_MODE.KEYBOARD);
    setInvoiceLineSort(INVOICE_LINE_SORT.REMAINING_DESC);
    setInvoiceHideComplete(true);
    setQueueIds([]);
    setActiveOrder(null);
    setLines([]);
    setScanValue('');
    resetScanFeedback();
    setLabelPrintPromptOpen(false);
    setLabelsToPrint(0);
    setPrinting(false);
  };

  const markShippedAndNotify = () => {
    if (!activeOrder || activeOrder.status === 'SHIPPED') return;
    if (packSessionKind !== PACK_SESSION.ORDERS) return;

    const packingShortfall = lines
      .filter((line) => line.remaining > 0)
      .map((line) => {
        const SKU = String(
          line.sku || (Array.isArray(line.matchKeys) ? line.matchKeys[0] : '') || '',
        ).trim();
        return { SKU, amount: Number(line.remaining) || 0 };
      })
      .filter((row) => row.SKU && row.amount > 0);

    const shortfallHint =
      packingShortfall.length > 0
        ? `\n\n${packingShortfall.length} line(s) were not fully verified by scan. The remaining quantity for each will be saved to the order's missing-items list and will not be sent to Xero.`
        : '';

    if (
      !window.confirm(
        `Mark order #${activeOrder.orderNumber} as SHIPPED and email the customer that their package is on its way?${shortfallHint}`,
      )
    ) {
      return;
    }
    setMarkingShipped(true);
    fetch(`${process.env.REACT_APP_API}/api/changeStatus/${activeOrder._id}`, {
      method: 'POST',
      cache: 'no-cache',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
      },
      redirect: 'follow',
      referrerPolicy: 'no-referrer',
      body: JSON.stringify({
        status: 'SHIPPED',
        ...(packingShortfall.length > 0 ? { packingShortfall } : {}),
      }),
    })
      .then((response) => {
        if (response.status === 200) {
          response.json().then((data) => {
            setActiveOrder(data);
            if (packingShortfall.length > 0) {
              toast.success(
                `Order marked shipped — customer notified. ${packingShortfall.length} missing line(s) added to the invoice; Xero update queued.`,
              );
            } else {
              toast.success('Order marked shipped — customer notified by email.');
            }
          });
        } else if (response.status === 404) {
          toast.error('Order or invoice not found.');
        } else {
          toast.error('Could not update status.');
        }
      })
      .catch(() => toast.error('Could not update status.'))
      .finally(() => setMarkingShipped(false));
  };

  const lineClassName = (line) =>
    [
      'packv2-line',
      line.remaining <= 0 ? 'packv2-line-done' : '',
      line.remaining > 0 ? 'packv2-line--open' : '',
      nextLine && line.id === nextLine.id ? 'packv2-line--next' : '',
      flashLineId === line.id ? 'packv2-line--flash' : '',
    ]
      .filter(Boolean)
      .join(' ');

  const renderInvoiceToolbar = () => {
    if (packSessionKind !== PACK_SESSION.IKEA_INVOICE || lines.length === 0) return null;
    const completeCount = lines.filter((l) => l.remaining <= 0).length;
    return (
      <div className="packv2-invoice-toolbar no-print">
        <div className="packv2-invoice-toolbar-sort" role="group" aria-label="Sort invoice lines">
          <span className="packv2-invoice-toolbar-label">Sort</span>
          <button
            type="button"
            className={
              invoiceLineSort === INVOICE_LINE_SORT.REMAINING_DESC
                ? 'packv2-invoice-chip packv2-invoice-chip--active'
                : 'packv2-invoice-chip'
            }
            onClick={() => setInvoiceLineSort(INVOICE_LINE_SORT.REMAINING_DESC)}
          >
            Still to scan
          </button>
          <button
            type="button"
            className={
              invoiceLineSort === INVOICE_LINE_SORT.REMAINING_ASC
                ? 'packv2-invoice-chip packv2-invoice-chip--active'
                : 'packv2-invoice-chip'
            }
            onClick={() => setInvoiceLineSort(INVOICE_LINE_SORT.REMAINING_ASC)}
          >
            Almost done
          </button>
          <button
            type="button"
            className={
              invoiceLineSort === INVOICE_LINE_SORT.PDF_ORDER
                ? 'packv2-invoice-chip packv2-invoice-chip--active'
                : 'packv2-invoice-chip'
            }
            onClick={() => setInvoiceLineSort(INVOICE_LINE_SORT.PDF_ORDER)}
          >
            Invoice order
          </button>
        </div>
        <button
          type="button"
          className={
            invoiceHideComplete
              ? 'packv2-invoice-chip packv2-invoice-chip--active'
              : 'packv2-invoice-chip'
          }
          onClick={() => setInvoiceHideComplete((on) => !on)}
        >
          {invoiceHideComplete
            ? `Incomplete only (${lines.length - completeCount} hidden)`
            : `Show all (${completeCount} done)`}
        </button>
      </div>
    );
  };

  const renderPackSummary = () => (
    <section className="packv2-pack-status-bar no-print">
      <div className="packv2-pack-progress">
        <div className="packv2-pack-progress-head">
          <span className="packv2-pack-progress-label">
            {packSessionKind === PACK_SESSION.IKEA_INVOICE ? 'Verified' : 'Packed'}
          </span>
          <span className="packv2-pack-progress-count">
            {progress.verified} / {progress.total}
          </span>
        </div>
        <div
          className="packv2-pack-progress-track"
          role="progressbar"
          aria-valuenow={progress.verified}
          aria-valuemin={0}
          aria-valuemax={progress.total}
        >
          <div
            className="packv2-pack-progress-fill"
            style={{ width: `${Math.round(progress.pct * 100)}%` }}
          />
        </div>
        <p className="packv2-pack-progress-meta">
          {progress.remaining > 0
            ? `${progress.remaining} unit(s) remaining · ${progress.openLines} line(s) open`
            : `All ${progress.total} item(s) verified`}
        </p>
      </div>

      {nextLine && !done ? (
        <div className="packv2-pack-next" aria-live="polite">
          <span className="packv2-pack-next-kicker">Scan next</span>
          <p className="packv2-pack-next-label">{nextLine.label}</p>
          {nextLine.sku ? (
            <p className="packv2-pack-next-sku">SKU {nextLine.sku}</p>
          ) : null}
          <span className="packv2-pack-next-qty">{nextLine.remaining} left</span>
        </div>
      ) : null}

      {done && lines.length > 0 ? (
        <div className="packv2-invoice-complete">
          <h3 className="packv2-invoice-complete-title">
            {packSessionKind === PACK_SESSION.IKEA_INVOICE
              ? 'Invoice verified'
              : 'Order fully scanned'}
          </h3>
          <p className="packv2-invoice-complete-body">
            {packSessionKind === PACK_SESSION.IKEA_INVOICE
              ? `Every line on ${invoiceFileMeta.invoiceNumber || 'this invoice'} matches what you scanned.`
              : 'All lines verified for this order.'}
          </p>
          {packSessionKind === PACK_SESSION.IKEA_INVOICE ? (
            <div className="packv2-btn-row">
              <button
                type="button"
                className="packv2-btn"
                onClick={() => {
                  resetToSelect();
                  setListMode(LIST_MODE.IKEA_BILL);
                }}
              >
                Create Xero bill from this PDF
              </button>
              <button type="button" className="packv2-btn packv2-btn--outline" onClick={resetToSelect}>
                Verify another invoice
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  );

  const renderScanControls = () => {
    const isInvoice = packSessionKind === PACK_SESSION.IKEA_INVOICE;
    const panelClass = [
      'no-print',
      'packv2-scan-panel',
      'packv2-pack-scan-dock',
      scanPanelState !== 'idle' ? `packv2-scan-panel--${scanPanelState}` : '',
      shake ? 'packv2-scan-shake' : '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={panelClass}>
        <div className="packv2-pack-scan-mode" role="tablist" aria-label="Scan mode">
          <button
            type="button"
            role="tab"
            aria-selected={scanMode === SCAN_MODE.CAMERA}
            className={
              scanMode === SCAN_MODE.CAMERA
                ? 'packv2-pack-scan-tab packv2-pack-scan-tab--active'
                : 'packv2-pack-scan-tab'
            }
            onClick={() => setScanMode(SCAN_MODE.CAMERA)}
          >
            Camera
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={scanMode === SCAN_MODE.KEYBOARD}
            className={
              scanMode === SCAN_MODE.KEYBOARD
                ? 'packv2-pack-scan-tab packv2-pack-scan-tab--active'
                : 'packv2-pack-scan-tab'
            }
            onClick={() => setScanMode(SCAN_MODE.KEYBOARD)}
          >
            Scanner gun
          </button>
        </div>

        {scanMode === SCAN_MODE.CAMERA ? (
          <div className="packv2-pack-scan-camera">
            <BarcodeCameraScanner key={packSessionKind} onDecoded={handleCameraDecoded} />
          </div>
        ) : (
          <form
            className="packv2-pack-scan-keyboard"
            onSubmit={(e) => {
              e.preventDefault();
              submitScan(scanRef.current?.value);
            }}
          >
            <label htmlFor="pack-scan-visible">
              {isInvoice ? 'Scan IKEA article barcode' : 'Barcode scanner input'}
            </label>
            <input
              ref={scanRef}
              id="pack-scan-visible"
              className="packv2-scan-input"
              type="text"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck={false}
              inputMode="numeric"
              enterKeyHint="done"
              disabled={loadingOrder || markingShipped}
              value={scanValue}
              onChange={(e) => setScanValue(e.target.value)}
              onKeyDown={onScanKeyDown}
              placeholder={isInvoice ? 'Scan or type article / EAN…' : 'Scan or type barcode…'}
            />
            <p className="packv2-scan-input-hint">Tap the field to type manually, or scan with your gun.</p>
            <button type="submit" className="packv2-btn packv2-pack-scan-submit">
              Confirm scan
            </button>
          </form>
        )}

        <div className="packv2-scan-feedback">
          <ScanProgressRing
            verified={progress.verified}
            total={progress.total}
            pct={progress.pct}
          />
          {lastScan ? (
            <div
              className={`packv2-scan-banner packv2-scan-banner--${lastScan.status}`}
              role="status"
              aria-live="polite"
            >
              {lastScan.status === 'success'
                ? `✓ ${lastScan.label} · ${lastScan.remaining} left`
                : lastScan.message || `✗ No match for “${lastScan.code}”`}
            </div>
          ) : (
            <p className="packv2-scan-hint">
              {isInvoice
                ? 'Scan each physical label until every line reads 0 / total.'
                : 'Scan items until every line is verified.'}
            </p>
          )}
          <label className="packv2-scan-beep-toggle">
            <input
              type="checkbox"
              checked={scanBeepOn}
              onChange={(e) => {
                setScanBeepOn(e.target.checked);
                localStorage.setItem('pack-scan-beep', e.target.checked ? 'true' : 'false');
              }}
            />
            Scan beep
          </label>
        </div>
      </div>
    );
  };

  const renderLineChecklist = () => (
    <>
      {lines.length === 0 ? (
        <p className="no-print">
          {packSessionKind === PACK_SESSION.IKEA_INVOICE
            ? 'No lines loaded.'
            : 'No cart lines or delivery note for this order.'}
        </p>
      ) : null}

      {packSessionKind === PACK_SESSION.IKEA_INVOICE &&
      invoiceHideComplete &&
      displayLines.length === 0 &&
      lines.length > 0 ? (
        <p className="packv2-hint no-print">Nothing left to scan on this invoice.</p>
      ) : null}

      <div className="packv2-lines packv2-lines--pack">
        {packingLineGroups.map((group) => {
          if (group.lines.length > 1) {
            const bundleKey = group.bundleGroupId || group.lines[0].id;
            const bundleHasNext = group.lines.some((l) => nextLine && l.id === nextLine.id);
            return (
              <div
                key={bundleKey}
                className={`packv2-line-bundle${bundleHasNext ? ' packv2-line-bundle--next' : ''}`}
              >
                <div className="packv2-line-bundle-header">
                  <div>
                    <div className="packv2-line-bundle-title">{group.lines[0].label}</div>
                    <div className="packv2-line-bundle-caption">
                      Multi-package product — scan each carton barcode ({group.lines.length} SKUs)
                    </div>
                  </div>
                </div>
                <div className="packv2-line-bundle-rows" role="group" aria-label={group.lines[0].label}>
                  {group.lines.map((line) => (
                    <div
                      key={line.id}
                      ref={(el) => {
                        if (el) lineRefs.current[line.id] = el;
                      }}
                      className={`${lineClassName(line)} packv2-line--bundle-part`}
                    >
                      <div>
                        <div className="packv2-line-label packv2-line-label--bundle-part">
                          {line.bundlePartIndex != null && line.bundleLineCount != null
                            ? `Package ${line.bundlePartIndex} of ${line.bundleLineCount}`
                            : 'Component'}
                        </div>
                        {line.sku ? (
                          <div className="packv2-line-sku">SKU: {line.sku}</div>
                        ) : null}
                        <div className="packv2-line-source">{line.source}</div>
                      </div>
                      <div className="packv2-line-count">
                        {line.remaining} / {line.total}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          }

          const line = group.lines[0];
          return (
            <div
              key={line.id}
              ref={(el) => {
                if (el) lineRefs.current[line.id] = el;
              }}
              className={lineClassName(line)}
            >
              <div>
                <div className="packv2-line-label">{line.label}</div>
                {line.sku ? <div className="packv2-line-sku">SKU: {line.sku}</div> : null}
                <div className="packv2-line-source">{line.source}</div>
              </div>
              <div className="packv2-line-count">
                {line.remaining} / {line.total}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );

  const renderLabelPrintDialog = () => {
    if (!labelPrintPromptOpen || !activeOrder) return null;
    return (
      <div
        className="packv2-label-print-overlay no-print"
        role="presentation"
        onClick={() => setLabelPrintPromptOpen(false)}
      >
        <div
          className="packv2-label-print-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="pack-label-print-title"
          onClick={(e) => e.stopPropagation()}
        >
          <h3 id="pack-label-print-title" className="packv2-label-print-title">
            Print box labels
          </h3>
          <p className="packv2-label-print-lead">
            Order #{activeOrder.orderNumber} — how many box labels do you need?
          </p>
          <label className="packv2-label-print-field" htmlFor="pack-label-print-count">
            Number of labels
          </label>
          <input
            id="pack-label-print-count"
            className="packv2-label-print-input"
            type="number"
            min={1}
            max={99}
            inputMode="numeric"
            value={labelPrintCount}
            onChange={(e) => setLabelPrintCount(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                confirmPrintLabels();
              }
            }}
            autoFocus
          />
          <div className="packv2-label-print-actions">
            <button
              type="button"
              className="packv2-btn packv2-btn--outline"
              onClick={() => setLabelPrintPromptOpen(false)}
            >
              Cancel
            </button>
            <button type="button" className="packv2-btn" onClick={confirmPrintLabels}>
              Print {Math.min(99, Math.max(1, Number(labelPrintCount) || 1))} label
              {Math.min(99, Math.max(1, Number(labelPrintCount) || 1)) === 1 ? '' : 's'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderPrintRoot = () => {
    if (!activeOrder || labelsToPrint < 1) return null;
    return (
      <div className="packing-print-root" aria-hidden="true">
        {Array.from({ length: labelsToPrint }, (_, index) => (
          <ShippingLabel
            key={`${activeOrder._id}-label-${index + 1}`}
            order={activeOrder}
            boxIndex={index + 1}
            boxTotal={labelsToPrint}
          />
        ))}
      </div>
    );
  };

  const renderPackActions = () => {
    if (packSessionKind !== PACK_SESSION.ORDERS || !activeOrder) return null;
    return (
      <footer className="packv2-pack-actions no-print">
        <div className="packv2-pack-actions-grid">
          <button
            type="button"
            className="packv2-pack-action-btn packv2-pack-action-btn--secondary"
            onClick={goPrev}
            disabled={activeIndex <= 0}
          >
            Previous
          </button>
          <button
            type="button"
            className="packv2-pack-action-btn packv2-pack-action-btn--ghost"
            onClick={goNext}
            disabled={activeIndex >= queueIds.length - 1}
          >
            Next
          </button>
          <button
            type="button"
            className="packv2-pack-action-btn packv2-pack-action-btn--primary"
            onClick={handlePrintLabel}
          >
            Print box label
          </button>
          <button
            type="button"
            className="packv2-pack-action-btn packv2-pack-action-btn--ship"
            onClick={markShippedAndNotify}
            disabled={markingShipped || activeOrder.status === 'SHIPPED'}
          >
            {markingShipped
              ? 'Updating…'
              : activeOrder.status === 'SHIPPED'
                ? 'Already shipped'
                : 'Mark shipped & email'}
          </button>
        </div>
      </footer>
    );
  };

  return (
    <div className={printing ? 'packv2 packv2-printing' : 'packv2'}>
      {!printing && <NavbarV2 />}

      {step === STEPS.SELECT && (
        <>
          <header className="packv2-header no-print">
            <h1 className="packv2-title">Packing / fulfillment</h1>
            <p className="packv2-subtitle">
              Pack orders from the queue, verify an IKEA Italia invoice PDF against what is in
              front of you, post the same PDF as one Xero supplier bill (stock / COGS), or send
              bulk warehouse emails.
            </p>
          </header>

          <main className="packv2-main no-print">
            <div
              className="packv2-list-toolbar"
              role="region"
              aria-label="List mode and primary action"
            >
              <div
                className="packv2-mode-toggle"
                role="radiogroup"
                aria-label="Packing mode"
              >
                <button
                  type="button"
                  role="radio"
                  aria-checked={listMode === LIST_MODE.PACK}
                  className={
                    listMode === LIST_MODE.PACK
                      ? 'packv2-mode-toggle-btn packv2-mode-toggle-btn--active'
                      : 'packv2-mode-toggle-btn'
                  }
                  onClick={() => setListMode(LIST_MODE.PACK)}
                >
                  Pack orders
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={listMode === LIST_MODE.INVOICE}
                  className={
                    listMode === LIST_MODE.INVOICE
                      ? 'packv2-mode-toggle-btn packv2-mode-toggle-btn--active'
                      : 'packv2-mode-toggle-btn'
                  }
                  onClick={() => setListMode(LIST_MODE.INVOICE)}
                >
                  IKEA invoice verify
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={listMode === LIST_MODE.IKEA_BILL}
                  className={
                    listMode === LIST_MODE.IKEA_BILL
                      ? 'packv2-mode-toggle-btn packv2-mode-toggle-btn--active'
                      : 'packv2-mode-toggle-btn'
                  }
                  onClick={() => setListMode(LIST_MODE.IKEA_BILL)}
                >
                  IKEA → Xero bill
                </button>
                <button
                  type="button"
                  role="radio"
                  aria-checked={listMode === LIST_MODE.NOTIFY}
                  className={
                    listMode === LIST_MODE.NOTIFY
                      ? 'packv2-mode-toggle-btn packv2-mode-toggle-btn--active'
                      : 'packv2-mode-toggle-btn'
                  }
                  onClick={() => setListMode(LIST_MODE.NOTIFY)}
                >
                  Customer emails (warehouse)
                </button>
              </div>
              <div className="packv2-list-toolbar-cta">
                {listMode === LIST_MODE.INVOICE || listMode === LIST_MODE.IKEA_BILL ? (
                  <span className="packv2-selected-pill">
                    Upload a PDF below — no order selection needed.
                  </span>
                ) : (
                  <>
                    <span className="packv2-selected-pill">
                      <strong>{selectedIds.length}</strong> selected
                    </span>
                    {listMode === LIST_MODE.PACK ? (
                      <button type="button" className="packv2-btn" onClick={startPacking}>
                        Start packing ({selectedIds.length})
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="packv2-btn"
                        onClick={sendWarehouseBulkEmails}
                        disabled={sendingWarehouseEmails}
                      >
                        {sendingWarehouseEmails
                          ? 'Sending…'
                          : `Send warehouse email (${selectedIds.length})`}
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>

            {listMode === LIST_MODE.INVOICE && (
              <div
                {...invoiceDropzone.getRootProps({
                  className: `packv2-card packv2-invoice-drop${
                    invoiceDropzone.isDragActive ? ' packv2-invoice-drop--active' : ''
                  }`,
                })}
              >
                <input {...invoiceDropzone.getInputProps()} />
                <label className="packv2-invoice-drop-label">
                  IKEA Italia invoice PDF (FATTURA)
                </label>
                <p className="packv2-invoice-drop-lead">
                  Drop the forwarded IKEA invoice here, or tap to browse. We read{' '}
                  <strong>N. articolo</strong> (8-digit SKUs) and <strong>Quantità</strong>, then you
                  scan physical barcodes until every line reaches zero.
                </p>
                <div className="packv2-invoice-drop-zone">
                  {invoiceParsing ? (
                    <div className="packv2-loading packv2-loading--compact">
                      <ClipLoader color="#C4603C" size={28} />
                      <span>Reading PDF…</span>
                    </div>
                  ) : (
                    <>
                      <span className="packv2-invoice-drop-badge">PDF</span>
                      <span className="packv2-invoice-drop-cta">
                        {invoiceDropzone.isDragActive
                          ? 'Release to upload'
                          : 'Choose PDF or drag here'}
                      </span>
                      <span className="packv2-invoice-drop-meta">
                        Example: MA_2026_0180834 · works best from IKEA’s PDF export
                      </span>
                    </>
                  )}
                </div>
              </div>
            )}

            {listMode === LIST_MODE.IKEA_BILL && (
              <>
                <div
                  {...ikeaBillDropzone.getRootProps({
                    className: `packv2-card packv2-invoice-drop${
                      ikeaBillDropzone.isDragActive ? ' packv2-invoice-drop--active' : ''
                    }`,
                  })}
                >
                  <input {...ikeaBillDropzone.getInputProps()} />
                  <label className="packv2-invoice-drop-label">
                    IKEA invoice → one draft supplier bill in Xero
                  </label>
                  <p className="packv2-invoice-drop-lead">
                    Same PDF as invoice verify. We read SKU (8-digit article) and quantity only. Unit
                    prices are <strong>not</strong> taken from the PDF — the backend uses your{' '}
                    <strong>product catalog</strong> (<code>price.currentPrice</code>, VAT-inclusive)
                    with <strong>8th Directive 0%</strong> tax on the bill.
                  </p>
                  <div className="packv2-invoice-drop-zone">
                    {invoiceParsing ? (
                      <div className="packv2-loading packv2-loading--compact">
                        <ClipLoader color="#C4603C" size={28} />
                        <span>Reading PDF…</span>
                      </div>
                    ) : (
                      <>
                        <span className="packv2-invoice-drop-badge">PDF</span>
                        <span className="packv2-invoice-drop-cta">
                          {ikeaBillDropzone.isDragActive
                            ? 'Release to upload'
                            : 'Choose PDF or drag here'}
                        </span>
                        <span className="packv2-invoice-drop-meta">
                          Text PDFs parse instantly; scanned/image PDFs use OCR (may take ~10s).
                          Supplier: Xero name contains “IKEA” or{' '}
                          <code style={{ fontSize: '0.85em' }}>XERO_IKEA_SUPPLIER_CONTACT_ID</code>
                        </span>
                      </>
                    )}
                  </div>
                </div>

                {ikeaBillData?.lines?.length > 0 ? (
                  <div className="packv2-card packv2-xero-preview" style={{ marginTop: '1rem' }}>
                    <div className="packv2-btn-row no-print" style={{ marginBottom: '0.75rem' }}>
                      <button
                        type="button"
                        className="packv2-btn"
                        onClick={postIkeaBillToXero}
                        disabled={
                          billPosting ||
                          ikeaBillPreviewLoading ||
                          !ikeaBillPreview?.ready
                        }
                      >
                        {billPosting ? 'Posting to Xero…' : 'Create draft bill in Xero'}
                      </button>
                      <button
                        type="button"
                        className="packv2-btn packv2-btn--outline"
                        onClick={() => {
                          setIkeaBillData(null);
                          setIkeaBillPreview(null);
                          setIkeaBillPreviewError(null);
                        }}
                        disabled={billPosting}
                      >
                        Clear preview
                      </button>
                    </div>

                    <div className="packv2-xero-preview-header">
                      <h2 className="packv2-xero-preview-title">Draft supplier bill preview</h2>
                      <p className="packv2-order-meta">
                        {ikeaBillData.invoiceNumber ? (
                          <>
                            <strong>{ikeaBillData.invoiceNumber}</strong>
                            {' · '}
                          </>
                        ) : null}
                        {ikeaBillData.filename}
                      </p>
                    </div>

                    {ikeaBillPreviewLoading ? (
                      <div className="packv2-loading packv2-loading--compact">
                        <ClipLoader color="#C4603C" size={28} />
                        <span>Resolving catalog prices for Xero preview…</span>
                      </div>
                    ) : ikeaBillPreviewError ? (
                      <p className="packv2-xero-preview-error">{ikeaBillPreviewError}</p>
                    ) : ikeaBillPreview ? (
                      <>
                        <dl className="packv2-xero-preview-meta">
                          <div>
                            <dt>Supplier</dt>
                            <dd>
                              {ikeaBillPreview.supplierName ||
                                (ikeaBillPreview.supplierWarning
                                  ? 'Could not resolve IKEA supplier in Xero'
                                  : 'IKEA supplier (from Xero on post)')}
                            </dd>
                          </div>
                          <div>
                            <dt>Reference</dt>
                            <dd>{ikeaBillPreview.reference}</dd>
                          </div>
                          <div>
                            <dt>Bill date</dt>
                            <dd>{ikeaBillPreview.date}</dd>
                          </div>
                          <div>
                            <dt>Due date</dt>
                            <dd>{ikeaBillPreview.dueDate}</dd>
                          </div>
                          <div>
                            <dt>Account</dt>
                            <dd>{ikeaBillPreview.accountCode} (COGS)</dd>
                          </div>
                          <div>
                            <dt>Tax</dt>
                            <dd>{ikeaBillPreview.taxTypeLabel || ikeaBillPreview.taxType || '—'}</dd>
                          </div>
                          <div>
                            <dt>Amounts</dt>
                            <dd>{ikeaBillPreview.lineAmountTypes} · {ikeaBillPreview.currencyCode}</dd>
                          </div>
                          <div>
                            <dt>Lines</dt>
                            <dd>{ikeaBillPreview.lineCount}</dd>
                          </div>
                          <div>
                            <dt>Status</dt>
                            <dd>{ikeaBillPreview.status}</dd>
                          </div>
                        </dl>

                        {ikeaBillPreview.supplierWarning ? (
                          <p className="packv2-hint packv2-xero-preview-warn">
                            Supplier lookup: {ikeaBillPreview.supplierWarning}
                          </p>
                        ) : null}

                        {!ikeaBillPreview.ready && ikeaBillPreview.manualLines?.length > 0 ? (
                          <p className="packv2-xero-preview-error">
                            Cannot post until every line is priced —{' '}
                            {ikeaBillPreview.manualLines.length === 1
                              ? ikeaBillPreview.manualLines[0].reason
                              : `${ikeaBillPreview.manualLines.length} line(s) unresolved (highlighted below).`}
                          </p>
                        ) : null}

                        <div className="packv2-table-wrap">
                          <table className="packv2-table packv2-table--xero-preview">
                            <thead>
                              <tr>
                                <th>SKU</th>
                                <th>Description</th>
                                <th className="packv2-table-num">Qty</th>
                                <th>Price source</th>
                                <th className="packv2-table-num">Unit (incl. VAT)</th>
                                <th className="packv2-table-num">Line total</th>
                              </tr>
                            </thead>
                            <tbody>
                              {ikeaBillPreview.lines.map((row, idx) => (
                                <tr
                                  key={`${row.sku}-${idx}`}
                                  className={row.priceError ? 'packv2-table-row--warn' : undefined}
                                >
                                  <td>{row.code || row.sku}</td>
                                  <td>
                                    {row.label}
                                    {row.priceError ? (
                                      <span className="packv2-line-price-error">{row.priceError}</span>
                                    ) : null}
                                  </td>
                                  <td className="packv2-table-num">{row.qty}</td>
                                  <td>
                                    {row.priceSource === 'xero_item'
                                      ? row.unitAmountInclusive != null
                                        ? 'Xero item (IKEA service)'
                                        : 'Xero item (price in Xero)'
                                      : row.priceSource === 'catalog'
                                        ? 'Product catalog'
                                        : '—'}
                                  </td>
                                  <td className="packv2-table-num">
                                    {row.unitAmountInclusive != null
                                      ? `€${formatMoney(row.unitAmountInclusive)}`
                                      : '—'}
                                  </td>
                                  <td className="packv2-table-num">
                                    {row.lineTotalInclusive != null
                                      ? `€${formatMoney(row.lineTotalInclusive)}`
                                      : '—'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                            <tfoot>
                              <tr>
                                <td colSpan={5} className="packv2-table-total-label">
                                  Subtotal (incl. VAT, 8th Directive 0%)
                                </td>
                                <td className="packv2-table-num packv2-table-total-value">
                                  €{formatMoney(ikeaBillPreview.subTotal)}
                                </td>
                              </tr>
                              <tr>
                                <td colSpan={5} className="packv2-table-total-label">
                                  Bill total
                                </td>
                                <td className="packv2-table-num packv2-table-total-value">
                                  €{formatMoney(ikeaBillPreview.total)}
                                </td>
                              </tr>
                            </tfoot>
                          </table>
                        </div>

                        <p className="packv2-hint" style={{ marginTop: '0.75rem' }}>
                          Unit prices come from your product catalog (<code>price.currentPrice</code>)
                          or, for IKEA service SKUs <code>500.008.75</code> and <code>600.008.51</code>,
                          from the matching Xero item purchase price. All lines must resolve before posting.
                        </p>
                      </>
                    ) : null}
                  </div>
                ) : null}
              </>
            )}

            {listMode === LIST_MODE.NOTIFY && (
              <div className="packv2-card">
                <label htmlFor="warehouse-notify-message">
                  Message to customers (included in the email)
                </label>
                <textarea
                  id="warehouse-notify-message"
                  className="packv2-textarea"
                  rows={6}
                  value={notifyMessage}
                  onChange={(e) => setNotifyMessage(e.target.value)}
                  placeholder={
                    'Example:\n' +
                    'Your order has arrived in our Malta warehouse and will go out for delivery soon. ' +
                    'We expect delivery on Monday.\n\n' +
                    'Thank you for your patience.'
                  }
                />
                <p className="packv2-hint">
                  The same text is sent to everyone selected. Order lines and a link
                  to view the order are added automatically.
                </p>
              </div>
            )}

            {listMode !== LIST_MODE.INVOICE && listMode !== LIST_MODE.IKEA_BILL && (
            <>
            <div className="packv2-filters">
              <div className="packv2-field">
                <label htmlFor="pack-search">Search</label>
                <input
                  id="pack-search"
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Order #, invoice #, name, email, phone…"
                />
              </div>
              <div className="packv2-field">
                <label htmlFor="pack-status">Status</label>
                <select
                  id="pack-status"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                >
                  <option value="">ALL</option>
                  <option value="SUBMITTED">SUBMITTED</option>
                  <option value="APPROVED">APPROVED</option>
                  <option value="DECLINED">DECLINED</option>
                  <option value="SHIPPED">SHIPPED</option>
                </select>
              </div>
              <button
                type="button"
                className="packv2-btn packv2-btn--outline"
                onClick={() => {
                  flushQuery();
                  flushFilter();
                  setPage(1);
                }}
              >
                Search now
              </button>
            </div>

            <div className="packv2-pagination">
              <button
                type="button"
                className="packv2-btn packv2-btn--outline"
                onClick={() => {
                  setPage((p) => Math.max(1, p - 1));
                }}
                disabled={page <= 1}
              >
                Prev page
              </button>
              <p className="packv2-page-num">{page}</p>
              <button
                type="button"
                className="packv2-btn packv2-btn--outline"
                onClick={() => {
                  setPage((p) => p + 1);
                }}
              >
                Next page
              </button>
            </div>

            {loadingList ? (
              <div className="packv2-loading">
                <ClipLoader color="#C4603C" />
              </div>
            ) : (
              <div className="packv2-card" style={{ padding: 0, overflow: 'hidden' }}>
                <div className="packv2-table-wrap">
                  <table className="packv2-table">
                    <thead>
                      <tr>
                        <th style={{ width: 48 }} aria-label="Select" />
                        <th>Order #</th>
                        <th>Date</th>
                        <th>Ship to</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {orders.map((row) => (
                        <tr
                          key={row._id}
                          className="packv2-table-row-selectable"
                          aria-selected={selectedIds.includes(row._id)}
                          onClick={() => toggleSelected(row._id)}
                        >
                          <td>
                            <input
                              type="checkbox"
                              className="packv2-check"
                              checked={selectedIds.includes(row._id)}
                              onChange={() => toggleSelected(row._id)}
                              onClick={(e) => e.stopPropagation()}
                              onKeyDown={(e) => e.stopPropagation()}
                              aria-label={`Select order ${row.orderNumber}`}
                            />
                          </td>
                          <td>{row.orderNumber}</td>
                          <td>
                            {new Intl.DateTimeFormat('en-GB', {
                              year: 'numeric',
                              month: '2-digit',
                              day: '2-digit',
                            }).format(new Date(row.date || row.createdAt))}
                          </td>
                          <td>
                            {row.deliveryname || row.name}{' '}
                            {row.deliverycity || row.city || ''}
                          </td>
                          <td>
                            <span
                              className={`packv2-status ${statusClass(row.status)}`}
                            >
                              {row.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
            </>
            )}
          </main>
        </>
      )}

      {step === STEPS.PACK && (
        <>
          <main className="packv2-main packv2-main--pack no-print">
            <div className="packv2-pack-shell">
              <header className="packv2-pack-context">
                <div className="packv2-pack-context-row">
                  <button
                    type="button"
                    className="packv2-pack-back-btn packv2-btn packv2-btn--outline"
                    onClick={resetToSelect}
                    aria-label="Back to list"
                  >
                    ← Back
                  </button>
                  <div className="packv2-pack-context-body">
                    {packSessionKind === PACK_SESSION.IKEA_INVOICE ? (
                      <>
                        <h2 className="packv2-pack-context-title">IKEA invoice verify</h2>
                        <p className="packv2-pack-context-meta">
                          {invoiceFileMeta.invoiceNumber ? (
                            <strong>{invoiceFileMeta.invoiceNumber}</strong>
                          ) : (
                            'Invoice number not found in PDF'
                          )}
                          {invoiceFileMeta.filename ? (
                            <>
                              {' · '}
                              <span className="packv2-invoice-filename">
                                {invoiceFileMeta.filename}
                              </span>
                            </>
                          ) : null}
                        </p>
                      </>
                    ) : loadingOrder ? (
                      <p className="packv2-pack-context-meta">Loading order…</p>
                    ) : activeOrder ? (
                      <>
                        <h2 className="packv2-pack-context-title">
                          Order #{activeOrder.orderNumber}
                        </h2>
                        <p className="packv2-pack-context-meta">
                          <span className={`packv2-status ${statusClass(activeOrder.status)}`}>
                            {activeOrder.status}
                          </span>
                          {' · '}
                          {(activeOrder.deliveryNote || []).length > 0 &&
                          (activeOrder.deliveryNote || []).reduce(
                            (s, x) => s + Number(x.amount || 0),
                            0,
                          ) > 0
                            ? 'Checklist from delivery note'
                            : 'Checklist from cart'}
                        </p>
                      </>
                    ) : (
                      <p className="packv2-pack-context-meta">No order loaded.</p>
                    )}
                  </div>
                  {packSessionKind === PACK_SESSION.ORDERS && queueIds.length > 0 ? (
                    <span className="packv2-pack-queue-badge">
                      {activeIndex + 1} / {queueIds.length}
                    </span>
                  ) : null}
                </div>
              </header>

              {loadingOrder ? (
                <div className="packv2-loading">
                  <ClipLoader color="#C4603C" />
                </div>
              ) : (
                <>
                  {lines.length > 0 ? renderScanControls() : null}

                  <div className="packv2-pack-scroll">
                    {lines.length > 0 ? renderPackSummary() : null}
                    {renderInvoiceToolbar()}

                    <section className="packv2-pack-checklist" aria-label="Packing checklist">
                      {renderLineChecklist()}
                    </section>

                    {packSessionKind === PACK_SESSION.ORDERS && activeOrder ? (
                      <details className="packv2-pack-label-drawer">
                        <summary>Label preview</summary>
                        <div className="packv2-label-preview">
                          <ShippingLabel order={activeOrder} />
                        </div>
                      </details>
                    ) : null}

                    {renderPackActions()}
                  </div>
                </>
              )}
            </div>
          </main>
        </>
      )}

      {renderLabelPrintDialog()}
      {renderPrintRoot()}
    </div>
  );
}

export default Packing;
